/**
 * Card component
 * Container for content sections
 */

import { type HTMLAttributes, type ReactNode } from "react";
import styles from "./Card.module.css";

/**
 * Card component props
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card content */
  children: ReactNode;
  /** Card title */
  title?: string;
  /** Card subtitle */
  subtitle?: string;
  /** Card padding */
  padding?: "none" | "small" | "medium" | "large";
  /** Card elevation/shadow */
  elevation?: "none" | "low" | "medium" | "high";
  /** Hoverable card */
  hoverable?: boolean;
}

/**
 * Card header component
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardHeader({
  children,
  className,
  ...props
}: CardHeaderProps): React.ReactElement {
  return (
    <div className={`${styles.header} ${className ?? ""}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Card body component
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardBody({
  children,
  className,
  ...props
}: CardBodyProps): React.ReactElement {
  return (
    <div className={`${styles.body} ${className ?? ""}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Card footer component
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardFooter({
  children,
  className,
  ...props
}: CardFooterProps): React.ReactElement {
  return (
    <div className={`${styles.footer} ${className ?? ""}`} {...props}>
      {children}
    </div>
  );
}

/**
 * Reusable card container component
 */
export function Card({
  children,
  title,
  subtitle,
  padding = "medium",
  elevation = "low",
  hoverable = false,
  className,
  ...props
}: CardProps): React.ReactElement {
  const classNames = [
    styles.card,
    styles[`padding-${padding}`],
    styles[`elevation-${elevation}`],
    hoverable ? styles.hoverable : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classNames} {...props}>
      {(title || subtitle) && (
        <CardHeader>
          {title && <h3 className={styles.title}>{title}</h3>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </CardHeader>
      )}
      {children}
    </div>
  );
}

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
